import { useState, useEffect } from "react";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import {
  collection,
  addDoc,
  query,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
  where,
} from "firebase/firestore";
import { COLLECTION_META, caregiverName } from "../utils/activityUtils";

export const useFirestore = (collectionName, childId = null) => {
  const { user, profile } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Real-time listener for data
  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    // All tracking collections require a childId — don't query without one
    if (!childId) {
      setItems([]);
      setLoading(false);
      return;
    }

    try {
      // Query only by childId — rules handle access control (owner + caregiver)
      const q = query(
        collection(db, collectionName),
        where("childId", "==", childId)
      );

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const data = [];
        querySnapshot.forEach((doc) => {
          data.push({ id: doc.id, ...doc.data() });
        });
        setItems(data);
        setLoading(false);
        setError(null);
      }, (err) => {
        console.error("Firestore error:", err);
        setError(err.message);
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (err) {
      console.error("Error setting up Firestore listener:", err);
      setError(err.message);
      setLoading(false);
    }
  }, [collectionName, user?.uid, childId]);

  // Add new item — also writes a lightweight entry to the activityFeed collection
  const addItem = async (item) => {
    if (!user?.uid) {
      throw new Error("User not authenticated");
    }
    const loggedAt = new Date().toISOString();
    try {
      await addDoc(collection(db, collectionName), {
        ...item,
        userId:    user.uid,
        childId:   item.childId || null,
        createdAt: loggedAt,
        // Store caregiver attribution directly on the document so section
        // tables can show "Logged by Dad" without a separate activityFeed lookup.
        loggedBy: {
          uid:  user.uid,
          name: profile?.displayName || caregiverName(user),
          role: profile?.role || '',
        },
      });
    } catch (err) {
      console.error("Error adding item:", err);
      setError(err.message);
      throw err;
    }

    // Write a lightweight activity feed entry (best-effort — never fails the main op)
    const meta = COLLECTION_META[collectionName];
    if (meta && item.childId) {
      try {
        await addDoc(collection(db, "activityFeed"), {
          childId: item.childId,
          collectionName,
          icon:    meta.icon,
          label:   meta.label,
          summary: meta.getSummary(item),
          eventTime: item.date || loggedAt,
          loggedAt,
          loggedBy: {
            uid:   user.uid,
            name:  profile?.displayName || caregiverName(user),
            role:  profile?.role || '',
            email: user.email || "",
          },
        });
      } catch (err) {
        // Non-critical — don't surface this to the user
        console.warn("Activity feed write failed:", err);
      }
    }
  };

  // Update item
  const updateItem = async (id, updates) => {
    try {
      await updateDoc(doc(db, collectionName, id), {
        ...updates,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error("Error updating item:", err);
      setError(err.message);
      throw err;
    }
  };

  // Delete item
  const deleteItem = async (id) => {
    try {
      await deleteDoc(doc(db, collectionName, id));
    } catch (err) {
      console.error("Error deleting item:", err);
      setError(err.message);
      throw err;
    }
  };

  return { items, loading, error, addItem, updateItem, deleteItem };
};
